import {
    loadStripe,
    StripeElementLocale,
    StripeElementsOptions
} from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import React, { PropsWithChildren, useMemo } from "react";
import i18n from "localization/i18n";
import { useTheme } from "hooks/useTheme";
import { ThemeOptions } from "store/view/types";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_API_KEY);

const getTrimmedStyle = (property: string) =>
    getComputedStyle(document.documentElement).getPropertyValue(property).trim();

const defaultOptions: StripeElementsOptions = {
    fonts: [
        {
            cssSrc: "https://fonts.googleapis.com/css?family=Montserrat:100,100i,200,200i,300,300i,400,400i,500,500i,600,600i,700,700i,800,800i,900,900i&amp;display=swap"
        }
    ],

    locale: i18n.language as StripeElementLocale,

    appearance: {
        labels: "floating",
        rules: {
            ".Tab": { borderRadius: "8px" },
            ".Input": { marginTop: "0.5625rem" },
            // `borderColor: transparent` (instead of `border: none`) keeps the
            // border's width/style constant on focus and just hides its color,
            // so the field doesn't change height when focused.
            ".Input:focus": { borderColor: "transparent" },
            ".Label--floating": { fontSize: "var(--fontSizeSm)" }
        }
    }
};

export interface StripeElementProps extends PropsWithChildren {
    options?: StripeElementsOptions;
}

export const StripeElement: React.FC<StripeElementProps> = ({
    children,
    options
}) => {
    const { theme } = useTheme();

    const variables = useMemo(
        () => ({
            colorPrimary: getTrimmedStyle("--theme-primary"),
            colorBackground: getTrimmedStyle(
                "--theme-buttons-and-form-fields-background"
            ),
            colorText: getTrimmedStyle(
                theme === ThemeOptions.Dark
                    ? "--theme-white"
                    : "--theme-on-surface"
            ),
            colorDanger: getTrimmedStyle("--theme-error"),
            fontFamily: getTrimmedStyle("--theme-font-family")
        }),
        [theme]
    );

    const mergedOptions = useMemo(
        () =>
            ({
                ...defaultOptions,
                ...options,
                appearance: {
                    ...defaultOptions.appearance,
                    ...options?.appearance,
                    variables: {
                        ...variables,
                        ...options?.appearance?.variables
                    }
                }
            }) as StripeElementsOptions,
        [options, variables]
    );

    return (
        <Elements
            key={theme}
            stripe={stripePromise}
            options={mergedOptions}
        >
            {children}
        </Elements>
    );
};
